import type { Env, VpnGatewayListResult, VpnGatewayRecord, VpnLinkAction, VpnPeerStatusResult } from '@fleet-cert-console/shared';
import { getIdToken } from '../auth/userManager.js';
import { getConfig } from '../config.js';
import { VPN_FIXTURE, VPN_PEER_STATUS_FIXTURE } from '../data/vpnFixture.js';

const useFixture = () => getConfig().useFixture;
const apiBase = () => getConfig().apiUrl;

// --- Session cache -------------------------------------------------------------------------------
// In-memory, per-tab cache so returning to a page is instant; cleared on page reload. The mutation
// helpers and each page's Refresh button call invalidateCache() to force a re-fetch.
const sessionCache = new Map<string, Promise<unknown>>();

/** Drop all cached reads (call after any write, or from a Refresh button). */
export function invalidateCache(): void {
  sessionCache.clear();
}

function withCache<T>(key: string, producer: () => Promise<T>): Promise<T> {
  const hit = sessionCache.get(key);
  if (hit) return hit as Promise<T>;
  const promise = producer().catch((err) => {
    sessionCache.delete(key); // never cache a failure
    throw err;
  });
  sessionCache.set(key, promise);
  return promise;
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function authHeaders(): Record<string, string> {
  const base: Record<string, string> = { 'content-type': 'application/json' };
  const token = getIdToken();
  return token ? { ...base, authorization: `Bearer ${token}` } : base;
}

// ---- VPN enrollment (vpn-gw-provisioning, cross-account) ----

let vpnState: Record<Env, VpnGatewayRecord[]> =
  typeof structuredClone === 'function' ? structuredClone(VPN_FIXTURE) : JSON.parse(JSON.stringify(VPN_FIXTURE));

const upsertVpnFixture = (env: Env, row: VpnGatewayRecord): void => {
  vpnState = {
    ...vpnState,
    [env]: [...vpnState[env].filter((g) => g.gatewayId !== row.gatewayId), row],
  };
};

export async function listVpnGateways(env: Env, opts: { fresh?: boolean } = {}): Promise<VpnGatewayListResult> {
  const key = `vpn:list:${env}`;
  if (opts.fresh) sessionCache.delete(key);
  return withCache(key, async () => {
    if (useFixture()) {
      await delay(120);
      return { items: vpnState[env] };
    }
    const res = await fetch(`${apiBase()}/vpn/gateways?env=${env}`, { headers: authHeaders() });
    if (!res.ok) throw new Error((await safeError(res)) ?? `VPN gateways request failed (${res.status})`);
    return (await res.json()) as VpnGatewayListResult;
  });
}

/** Uncached on purpose — this drives the enrollment status polling. */
export async function getVpnGateway(env: Env, gatewayId: string): Promise<VpnGatewayRecord | null> {
  if (useFixture()) {
    await delay(100);
    return vpnState[env].find((g) => g.gatewayId === gatewayId) ?? null;
  }
  const res = await fetch(`${apiBase()}/vpn/gateways?env=${env}&gatewayId=${encodeURIComponent(gatewayId)}`, {
    headers: authHeaders(),
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error((await safeError(res)) ?? `VPN gateway request failed (${res.status})`);
  return ((await res.json()) as { item: VpnGatewayRecord }).item;
}

/** Live WireGuard peer state from the hub (SSM round trip, ~1-2 s). Uncached on purpose — the BFF
 *  holds a short micro-cache; every console call should reflect the hub as it is now. */
export async function fetchVpnPeerStatus(env: Env): Promise<VpnPeerStatusResult> {
  if (useFixture()) {
    await delay(400);
    return { peers: VPN_PEER_STATUS_FIXTURE[env], fetchedAt: new Date().toISOString() };
  }
  const res = await fetch(`${apiBase()}/vpn/peer-status?env=${env}`, { headers: authHeaders() });
  if (!res.ok) throw new Error((await safeError(res)) ?? `VPN peer status failed (${res.status})`);
  return (await res.json()) as VpnPeerStatusResult;
}

export async function vpnInitiate(env: Env, gatewayId: string): Promise<void> {
  if (useFixture()) {
    await delay(200);
    const existing = vpnState[env].find((g) => g.gatewayId === gatewayId);
    upsertVpnFixture(env, {
      ...existing,
      gatewayId,
      vpnStatus: 'requested',
      gatewayPubKey: existing?.gatewayPubKey ?? 'DEMOPUBKEYbmV3IGdhdGV3YXkgZGVtbyBrZXk=',
      updatedAt: new Date().toISOString(),
    });
    invalidateCache();
    return;
  }
  const res = await fetch(`${apiBase()}/vpn/initiate`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ env, gatewayId }),
  });
  if (!res.ok) throw new Error((await safeError(res)) ?? `VPN initiate failed (${res.status})`);
  invalidateCache();
}

export async function vpnApprove(
  env: Env,
  gatewayId: string,
  approvalReason: string,
  dryRun: boolean,
): Promise<void> {
  if (useFixture()) {
    await delay(300);
    if (!dryRun) {
      const existing = vpnState[env].find((g) => g.gatewayId === gatewayId);
      upsertVpnFixture(env, {
        ...existing,
        gatewayId,
        vpnStatus: 'applied',
        vpnIp: existing?.vpnIp ?? '100.100.1.9',
        updatedAt: new Date().toISOString(),
      });
    }
    invalidateCache();
    return;
  }
  const res = await fetch(`${apiBase()}/vpn/approve`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ env, gatewayId, approvalReason, dryRun }),
  });
  if (!res.ok) throw new Error((await safeError(res)) ?? `VPN approve failed (${res.status})`);
  invalidateCache();
}

/**
 * Operator link action on an applied gateway (POST /vpn/link): `up` / `down` / `status` send `{}`;
 * `autostart` sends `{ enabled }`. The BFF answers 409 "gateway is not applied" for any other
 * status; the gateway itself answers within ~10 s and the control plane writes the vpnLink* /
 * version fields onto the row, so callers re-poll the list after this resolves.
 */
export async function vpnLink(env: Env, gatewayId: string, action: VpnLinkAction, enabled?: boolean): Promise<void> {
  if (useFixture()) {
    await delay(200);
    const existing = vpnState[env].find((g) => g.gatewayId === gatewayId);
    if (!existing || existing.vpnStatus !== 'applied') throw new Error('gateway is not applied');
    const now = new Date().toISOString();
    const execId = Date.now() % 2147483647;
    const patch: Partial<VpnGatewayRecord> =
      action === 'up'
        ? { vpnLinkState: 'up', vpnLinkConnected: true, vpnLinkHandshakeAgoSec: 3, vpnHeld: false }
        : action === 'down'
          ? { vpnLinkState: 'down', vpnLinkConnected: false, vpnLinkHandshakeAgoSec: null, vpnHeld: true }
          : action === 'autostart'
            ? { vpnAutostart: enabled === true }
            : {};
    upsertVpnFixture(env, {
      ...existing,
      ...patch,
      vpnLinkUpdatedAt: now,
      vpnLinkLastAction: action,
      vpnLinkLastActionAt: now,
      vpnLinkLastExecId: execId,
      vpnLastExecId: execId,
      updatedAt: now,
    });
    invalidateCache();
    return;
  }
  const res = await fetch(`${apiBase()}/vpn/link`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(action === 'autostart' ? { env, gatewayId, action, enabled } : { env, gatewayId, action }),
  });
  if (!res.ok) throw new Error((await safeError(res)) ?? `VPN ${action} failed (${res.status})`);
  invalidateCache();
}

export interface VpnTerminalSessionResult {
  sessionId: string;
  streamUrl: string;
  tokenValue: string;
}

/** Broker an SSM session that lands on the gateway (admin tier). Never cached. */
export async function vpnTerminalSession(env: Env, gatewayId: string): Promise<VpnTerminalSessionResult> {
  if (useFixture()) {
    await delay(200);
    throw new Error('The in-browser terminal needs the live BFF (demo mode has no hub).');
  }
  const res = await fetch(`${apiBase()}/vpn/terminal-session`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ env, gatewayId }),
  });
  if (!res.ok) throw new Error((await safeError(res)) ?? `Terminal session failed (${res.status})`);
  return (await res.json()) as VpnTerminalSessionResult;
}

/** Fire-and-forget close on page unload (sendBeacon; falls back to fetch keepalive). */
export function vpnTerminalSessionClose(env: Env, sessionId: string): void {
  if (useFixture()) return;
  const url = `${apiBase()}/vpn/terminal-session/close`;
  const body = JSON.stringify({ env, sessionId });
  // sendBeacon cannot carry the Authorization header; use keepalive fetch (works on unload).
  void fetch(url, { method: 'POST', headers: authHeaders(), body, keepalive: true }).catch(() => {});
}

export async function vpnFilePushPresign(
  env: Env,
  gatewayId: string,
  fileName: string,
  sizeBytes: number,
): Promise<{ uploadUrl: string; s3Key: string }> {
  if (useFixture()) {
    await delay(200);
    throw new Error('File push needs the live BFF (demo mode has no hub).');
  }
  const res = await fetch(`${apiBase()}/vpn/file-push/presign`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ env, gatewayId, fileName, sizeBytes }),
  });
  if (!res.ok) throw new Error((await safeError(res)) ?? `Presign failed (${res.status})`);
  return (await res.json()) as { uploadUrl: string; s3Key: string };
}

export async function vpnFilePushStart(
  env: Env,
  gatewayId: string,
  s3Key: string,
  destPath: string,
): Promise<string> {
  const res = await fetch(`${apiBase()}/vpn/file-push`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ env, gatewayId, s3Key, destPath }),
  });
  if (!res.ok) throw new Error((await safeError(res)) ?? `File push failed (${res.status})`);
  return ((await res.json()) as { commandId: string }).commandId;
}

export async function vpnFilePushStatus(
  env: Env,
  commandId: string,
): Promise<{ status: 'pending' | 'success' | 'failed'; stderr?: string }> {
  const res = await fetch(`${apiBase()}/vpn/file-push?env=${env}&commandId=${encodeURIComponent(commandId)}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error((await safeError(res)) ?? `File push status failed (${res.status})`);
  return (await res.json()) as { status: 'pending' | 'success' | 'failed'; stderr?: string };
}

async function safeError(res: Response): Promise<string | undefined> {
  try {
    return ((await res.json()) as { error?: string }).error;
  } catch {
    return undefined;
  }
}
